// Service layer for the AyushPay Credit Line Subscribers module.
// Wired to the loan backend (internalapi v4, ops + superadmin).

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getApiRole = (uiRole) => {
  const roleMapping = {
    SuperAdmin: 'superadmin',
    Finance: 'finance',
    Ops: 'ops',
    Support: 'support',
  };
  return roleMapping[uiRole] || 'ops';
};

const makeRequest = async (endpoint, options = {}) => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    throw new Error('No authentication token found. Please login first.');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 12000);

  try {
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method: options.method || 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
      body: options.body,
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = errorText || 'Unknown API error';
      try {
        const parsed = JSON.parse(errorText);
        if (Array.isArray(parsed?.msg)) {
          errorMessage = parsed.msg.map((m) => m.value).filter(Boolean).join(' ') || errorText; // ok
        } else {
          errorMessage = parsed?.msg?.error || parsed?.msg?.status || errorText;
        }
      } catch {
        // keep raw text
      }
      throw new Error(errorMessage);
    }

    return response.json();
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please try again.');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const getCreditLineSubscribersList = async (role, params = {}) => {
  const apiRole = getApiRole(role);
  const qs = new URLSearchParams();

  if (params.page) qs.set('page', params.page);
  if (params.items) qs.set('items', params.items);
  if (params.search) qs.set('search', params.search);
  if (params.status) qs.set('status', params.status);

  const query = qs.toString();
  return makeRequest(`/internal/apis/v4/${apiRole}/credit-line-subscribers/list/${query ? `?${query}` : ''}`);
};

export const getCreditLineSubscriberDetail = async (role, applicationId) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/credit-line-subscribers/${applicationId}/detail/`);
};

export const toggleCreditPermission = async (role, applicationId, scope, enabled) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/credit-line-subscribers/${applicationId}/toggle-credit/`, {
    method: 'PATCH',
    body: JSON.stringify({ scope, enabled }),
  });
};

export const getCreditPermissions = async (role, applicationId) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/credit-line-subscribers/${applicationId}/permissions/`);
};

export const getBulkToggleCount = async (role, scope) => {
  const apiRole = getApiRole(role);
  const qs = new URLSearchParams({ scope });
  return makeRequest(`/internal/apis/v4/${apiRole}/credit-line-subscribers/bulk-toggle-credit/count/?${qs.toString()}`);
};

export const bulkToggleCreditPermission = async (role, scope, enabled) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/credit-line-subscribers/bulk-toggle-credit/`, {
    method: 'POST',
    body: JSON.stringify({ scope, enabled }),
  });
};

export const getBulkToggleJobStatus = async (role, jobId) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/credit-line-subscribers/bulk-toggle-credit/${jobId}/status/`);
};
